import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Card, CardContent } from '@mui/material';
import CellTowerOutlinedIcon from '@mui/icons-material/CellTowerOutlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';

import { EpNodeGroup } from '@bumble/app/__generated__/data-contracts';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  EndpointNodeGroupDhcp,
  EndpointNodeGroupVrrp,
} from '@bumble/app/components/endpoint-node-group';

const columns: GridColDef[] = [
  {
    field: 'ep_node_group_name',
    headerName: 'Node Group Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'ep_node_group_id',
    headerName: 'Node Group Id',
    width: 200,
    align: 'left',
    disableColumnMenu: true,
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
];

const columnVisible = {
  ep_node_group_name: true,
  ep_node_group_id: true,
  created_at: true,
};

export const EndpointNodeGroups = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [selectedGroup, setSelectedGroup] = useState<EpNodeGroup | null>(null);
  const [showDhcpDialog, setShowDhcpDialog] = useState<boolean>(false);
  const [showVrrpDialog, setShowVrrpDialog] = useState<boolean>(false);

  const api = useApi();

  const { isLoading, data, refetch } = useQuery(
    ['endpointNodeGroups'],
    api.listEpNodeGroupApiV1ListEndpointNodeGroupGet.bind(null, {}),
    { enabled: true },
  );

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Endpoint Node Groups.');
  };

  const selectedRowGroup = useMemo(() => {
    if (rowSelectionModel.length !== 1) return null;
    const itemId = rowSelectionModel[0].toString();
    return data?.data.find((item) => item.ep_node_group_id === itemId) || null;
  }, [data, rowSelectionModel]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedGroup(selectedRowGroup);
    } else {
      setSelectedGroup(null);
    }
  }, [rowSelectionModel, selectedRowGroup]);

  const {
    // isLoading: detailLoading,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['endpointNodeGroupDetail'],
    api.getEpNodeGroupDetailsApiV1GetEndpointNodeGroupDetailsGet.bind(null, {
      ep_node_group_id: selectedGroup?.ep_node_group_id || '',
    }),
    { enabled: false, cacheTime: 0 },
  );

  useEffect(() => {
    if (selectedGroup) {
      detailRefetch();
    }
  }, [detailRefetch, selectedGroup]);

  const handleDhcpSubmit = () => {
    detailRefetch();
    setShowDhcpDialog(false);
  };

  const handleVrrpSubmit = () => {
    detailRefetch();
    setShowVrrpDialog(false);
  };

  return (
    <PageContent>
      <PageHeader
        pageName="Manage Endpoint Node Groups"
        breadcrumbs={[{ label: 'Endpoint Nodes', to: '/endpoint-nodes' }]}
        count={data?.data?.length || 0}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={
                data?.data.map((item) => {
                  return {
                    ...item,
                    id: item.ep_node_group_id,
                  };
                }) || []
              }
              columns={columns}
              handleSelectionChange={handleSelectionChange}
              rowSelectionModel={rowSelectionModel}
              itemsSelected={rowSelectionModel.length === 1}
              columnVisibilityModel={columnVisible}
              refreshData={handleDataRefetch}
              menuItems={[
                {
                  label: 'Setup DHCP Relay',
                  icon: <RouterOutlinedIcon color="info" fontSize="small" />,
                  onClick: () => setShowDhcpDialog(true),
                },
                {
                  label: 'Setup VRRP',
                  icon: <CellTowerOutlinedIcon color="info" fontSize="small" />,
                  onClick: () => setShowVrrpDialog(true),
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
      {showDhcpDialog && selectedGroup && (
        <DialogStandard
          open={showDhcpDialog}
          handleClose={() => setShowDhcpDialog(false)}
          title="Setup DHCP Relay"
        >
          <EndpointNodeGroupDhcp
            onSubmit={handleDhcpSubmit}
            endpointNodeGroup={selectedGroup?.ep_node_group_id}
            endpointDhcpEnabled={detailData?.data?.dhcp_relay_enabled || false}
            endpointDhcp={detailData?.data?.dhcp_relay_server_ip || ''}
          />
        </DialogStandard>
      )}
      {showVrrpDialog && selectedGroup && (
        <DialogStandard
          open={showVrrpDialog}
          handleClose={() => setShowVrrpDialog(false)}
          title="Setup VRRP"
        >
          <EndpointNodeGroupVrrp
            onSubmit={handleVrrpSubmit}
            endpointNodeGroup={selectedGroup?.ep_node_group_id}
            endpointVrrpEnabled={detailData?.data?.vrrp_enabled || false}
            endpointVrrp={detailData?.data?.vrrp_virtual_ip || ''}
          />
        </DialogStandard>
      )}
    </PageContent>
  );
};
